import React from 'react';
import './RankedProgressList.scss';

export interface RankedProgressListItem {
  id: string;
  label: string;
  count: number;
  percent: number;
  color: string;
}

export interface RankedProgressListProps {
  items: RankedProgressListItem[];
  formatCount?: (count: number) => string;
  formatPercent?: (percent: number) => string;
}

const defaultFormatCount = (count: number) => count.toLocaleString('en-US');
const defaultFormatPercent = (percent: number) => `${Math.round(percent)}%`;

const RankedProgressList: React.FC<RankedProgressListProps> = ({
  items,
  formatCount = defaultFormatCount,
  formatPercent = defaultFormatPercent,
}) => {
  const visibleItems = items.filter((item) => item.percent > 0);

  if (visibleItems.length === 0) return null;

  return (
    <ul className="ranked-progress-list">
      {visibleItems.map((item) => {
        const formattedCount = formatCount(item.count);
        const formattedPercent = formatPercent(item.percent);
        const accessibleLabel = `${item.label}: ${formattedCount} (${formattedPercent})`;

        return (
          <li
            className="ranked-progress-list__item"
            key={item.id}
            aria-label={accessibleLabel}
            data-testid={`ranked-progress-list-item-${item.id}`}
          >
            <div className="ranked-progress-list__row" aria-hidden="true">
              <span className="ranked-progress-list__label">{item.label}</span>
              <span className="ranked-progress-list__count">{formattedCount}</span>
              <span className="ranked-progress-list__percent">{formattedPercent}</span>
            </div>
            <div className="ranked-progress-list__track" aria-hidden="true">
              <div
                className="ranked-progress-list__fill"
                style={{
                  width: `${Math.min(item.percent, 100)}%`,
                  backgroundColor: item.color,
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default RankedProgressList;
